import { API_BASE_URL } from '../config.js';

class FeeReminderService {
  constructor() {
    // Use the same backend host as BaseApiService
        this.baseURL = API_BASE_URL;
        
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true',
    };

    const token = localStorage.getItem('authToken');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Send fee reminders to students with pending fees
   * @param {Object} data - Reminder data
   * @param {string} data.orgId - Organization ID
   * @param {string} data.academicYear - Academic year (optional, for filtering)
   * @param {string} data.class - Class name (optional, for school)
   * @param {string} data.department - Department name (optional, for college)
   * @returns {Promise} - API response
   */
  async sendFeeReminders(data) {
    try {
      const url = `${this.baseURL}/api/notifications/admin/fee-alert`;
      
      // Build the request payload with required fields
      const payload = {
        orgId: data.orgId,
        title: 'Fee Payment Reminder',
        message: 'This is a reminder to pay your pending fees. Please visit the portal to make payment.',
        academicYear: data.academicYear,
        class: data.class,
        department: data.department,
        section: data.section,
        semester: data.semester
      };

      // Remove undefined fields
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

      console.log('Sending fee reminders with data:', payload);
      console.log('Request URL:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      console.log('Fee reminder response:', result);
      return result;
    } catch (error) {
      console.error('Error sending fee reminders:', error);
      throw error;
    }
  }

  /**
   * Get reminder history for an organization
   * @param {string} orgId - Organization ID
   * @returns {Promise} - API response with reminder history
   */
  async getReminderHistory(orgId) {
    try {
      const url = `${this.baseURL}/api/reminders/history/${orgId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching reminder history:', error);
      throw error;
    }
  }

  /**
   * Check if reminder was already sent today
   * @param {string} orgId - Organization ID
   * @returns {boolean} - True if reminder was sent today
   */
  canSendReminder(orgId) {
    const lastReminderKey = `fee_reminder_last_sent_${orgId}`;
    const lastSentDate = localStorage.getItem(lastReminderKey);
    
    if (!lastSentDate) {
      return true;
    }

    const today = new Date().toDateString();
    const lastDate = new Date(lastSentDate).toDateString();
    
    return today !== lastDate;
  }

  /**
   * Record that reminder was sent today
   * @param {string} orgId - Organization ID
   */
  recordReminderSent(orgId) {
    const lastReminderKey = `fee_reminder_last_sent_${orgId}`;
    localStorage.setItem(lastReminderKey, new Date().toISOString());
  }

  /**
   * Get time remaining until next reminder can be sent
   * @param {string} orgId - Organization ID
   * @returns {Object} - { canSend: boolean, hoursRemaining: number, minutesRemaining: number }
   */
  getTimeUntilNextReminder(orgId) {
    const lastReminderKey = `fee_reminder_last_sent_${orgId}`;
    const lastSentDate = localStorage.getItem(lastReminderKey);

    if (!lastSentDate) {
      return { canSend: true, hoursRemaining: 0, minutesRemaining: 0 };
    }

    const lastDate = new Date(lastSentDate);
    const now = new Date();
    const nextAllowedTime = new Date(lastDate.getTime() + 24 * 60 * 60 * 1000);

    if (now >= nextAllowedTime) {
      return { canSend: true, hoursRemaining: 0, minutesRemaining: 0 };
    }

    const diffMs = nextAllowedTime - now;
    const hoursRemaining = Math.floor(diffMs / (1000 * 60 * 60));
    const minutesRemaining = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return { canSend: false, hoursRemaining, minutesRemaining };
  }

  /**
   * Reset reminder for testing purposes (admin only)
   * @param {string} orgId - Organization ID
   */
  resetReminderForTesting(orgId) {
    const lastReminderKey = `fee_reminder_last_sent_${orgId}`;
    localStorage.removeItem(lastReminderKey);
  }
}

const feeReminderService = new FeeReminderService();
export default feeReminderService;
